import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { playbookListEntryPoint } from '@atlassian/jira-playbooks-list-screen/src/ui/playbook-list/entrypoint.tsx';
import { playbookLogsEntryPoint } from '@atlassian/jira-playbooks-list-screen/src/ui/playbook-logs/entrypoint.tsx';
import { playbookUsageEntryPoint } from '@atlassian/jira-playbooks-list-screen/src/ui/playbook-usage/entrypoint.tsx';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { fg } from '@atlassian/jira-feature-gating';
import type { PlaybookUsagePageRefetchQuery$variables } from '@atlassian/jira-playbooks-list-screen/src/ui/playbook-usage/paginated-playbook-usage-table/__generated__/PlaybookUsagePageRefetchQuery.graphql';

// This is for filters on type JiraPlaybookListFilter
const getListFilters = (name?: string, label?: string) => {
	const filters: { name?: string; labels?: string[] } = {};
	if (name) {
		filters.name = name;
	}
	if (label) {
		filters.labels = [label];
	}
	return Object.keys(filters).length > 0 ? filters : null;
};

function getUsageFilter({ name, status }: { name?: string; status?: string }) {
	const filter: Required<Filter> = {
		endTime: null,
		name: null,
		startTime: null,
		state: null,
		stepStatus: null,
		stepType: null,
	};

	if (name) filter.name = name;
	if (status === 'ENABLED' || status === 'DISABLED') filter.state = status;

	const cleanedFilter = Object.fromEntries(Object.entries(filter).filter(([_, v]) => v !== null));
	return Object.values(cleanedFilter).some(Boolean) ? cleanedFilter : null;
}

function getListPageEntrypoint(
	tab: string,
	cloudId: string,
	projectKey: string,
	name: string,
	label: string,
	usageFilter: Filter,
	getFilter: () => { name?: string; state?: string } | null,
) {
	switch (tab) {
		case 'logs':
			return {
				preloadedPlaybookLogsEntryPoint: {
					entryPoint: playbookLogsEntryPoint,
					entryPointParams: {
						cloudId,
						projectKey,
						first: 20,
						filter: name ? { name } : null,
					},
				},
			};
		case 'usage':
			return {
				preloadedPlaybookUsageEntryPoint: {
					entryPoint: playbookUsageEntryPoint,
					entryPointParams: {
						cloudId,
						projectKey,
						first: 20,
						usageFilter,
					},
				},
			};
		default:
			return {
				preloadedPlaybookListEntryPoint: {
					entryPoint: playbookListEntryPoint,
					entryPointParams: {
						cloudId,
						projectKey,
						first: 20,
						...(fg('phantom-3292-playbook-labels')
							? { filters: getListFilters(name, label) }
							: { filter: getFilter() }),
						isFetchingLabels: fg('phantom-3292-playbook-labels'),
					},
				},
			};
	}
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore Argument of type '{ root: ...; getPreloadProps: ({ context, tenantContext }: EntryPointRouteParams) => any; }' is not assignable to parameter of type 'never'
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const playbookListViewEntryPoint = createEntryPoint({
	root: JSResourceForInteraction(
		() => import(/* webpackChunkName: "async-spa-apps-playbooks-list" */ './src'),
	),
	// This has been causing build errors in unrelated code. Broadening type to fix build.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	getPreloadProps: ({ context, tenantContext }: EntryPointRouteParams): any => {
		const { projectKey } = context.match.params;
		const { tab = 'list', name = '', status, label = '' } = context.query;

		if (!projectKey) {
			throw new Error('projectKey is required');
		}

		const getFilter = (): FilterType => {
			if (fg('phantom-playbook-save-as-draft')) {
				const filter: FilterType = {};
				const state = getStateFromStatus(status);
				if (name) {
					filter.name = name;
				}
				if (state) {
					filter.state = state;
				}
				return Object.values(filter).some(Boolean) ? filter : null;
			}
			return name ? { name } : null;
		};

		const usageFilter = fg('enable_usage_tab') ? getUsageFilter({ name, status }) : null;

		let entryPoints;
		if (fg('enable_usage_tab')) {
			entryPoints = getListPageEntrypoint(
				tab,
				tenantContext.cloudId,
				projectKey,
				name,
				label,
				usageFilter,
				getFilter,
			);
		} else {
			// In case where tab is logs, we need to load the logs entry point
			// For other cases, fallback to the list entry point. This matches the current logic in the UI component.
			entryPoints =
				tab === 'logs'
					? {
							preloadedPlaybookLogsEntryPoint: {
								entryPoint: playbookLogsEntryPoint,
								entryPointParams: {
									cloudId: tenantContext.cloudId,
									projectKey,
									first: 20,
									filter: name ? { name } : null,
								},
							},
						}
					: {
							preloadedPlaybookListEntryPoint: {
								entryPoint: playbookListEntryPoint,
								entryPointParams: {
									cloudId: tenantContext.cloudId,
									projectKey,
									first: 20,
									...(fg('phantom-3292-playbook-labels')
										? { filters: getListFilters(name, label) }
										: { filter: getFilter() }),
									isFetchingLabels: fg('phantom-3292-playbook-labels'),
								},
							},
						};
		}

		return {
			extraProps: {
				accountId: tenantContext.atlassianAccountId,
				cloudId: tenantContext.cloudId,
				projectId: context.query.projectId,
				projectKey: context.match.params.projectKey,
			},
			entryPoints,
		};
	},
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
}) as any;

export default playbookListViewEntryPoint;

type FilterType = { name?: string; state?: string } | null;

type Filter = PlaybookUsagePageRefetchQuery$variables['filters'];

const getStateFromStatus = (status: string): string | undefined => {
	switch (status) {
		case 'on':
			return 'ENABLED';
		case 'off':
			return 'DISABLED';
		case 'draft':
			return 'DRAFT';
		default:
			return undefined;
	}
};
